/**
 * Runtime helpers for public widget + MediKaLLink paths from ``frontendapi.json``
 * (see ``src/config/loadFrontendConfig.ts``). The FastAPI server uses ``backend/backendapi.json`` only.
 */

import { frontendApiConfig, type FrontendApiConfig } from '../config/loadFrontendConfig'
import type { SessionChatWireMessage } from '../utils/chatHistoryPersistence'

const apis = frontendApiConfig

function trimTrailingSlashes(s: string): string {
  return s.replace(/\/+$/, '')
}

function trimBaseUrl(base: string): string {
  return trimTrailingSlashes(base.trim())
}

/** Set localStorage `voicnav_debug_session` to `1` to log chat/command + session chat-history traffic in the browser console. */
export function voicnavSessionClientDebug(): boolean {
  try {
    return (
      typeof globalThis !== 'undefined' &&
      (globalThis as unknown as { localStorage?: Storage }).localStorage?.getItem('voicnav_debug_session') === '1'
    )
  } catch {
    return false
  }
}

type MedicalLinkRest = FrontendApiConfig['medicalLink']['rest']

export type VoiceNavVersionInfo = {
  status: string
  service: string
  version: string
  started_at: string
  started_at_label: string
  transcribe_mode?: number
}

export type TranscribeVoiceResponse = {
  full_transcript: string
  display_transcript?: string
  rule_normalized_transcript?: string
  llm_normalized_transcript?: string
  assistant_messages?: Array<{
    role: string
    text: string
    metadata?: Record<string, unknown>
    ui_type?: string
    options?: Array<{ selection_id: string; label: string; dob: string; gender?: string }>
  }>
  llm_token_usage?: Record<string, unknown> | null
}

export function getDefaultVoiceNavApiBaseFromCatalog(): string {
  return trimBaseUrl(apis.defaults.voiceNavApiBase)
}

export function getDefaultWidgetPublicUrlFromCatalog(): string {
  return trimBaseUrl(apis.defaults.widgetPublicUrl)
}

/** REST paths served by the VoiceNav backend (suffix for `VITE_API_BASE_URL`). */
export function buildVoiceNavUrl(
  baseUrl: string,
  name: keyof FrontendApiConfig['voiceNav'],
): string {
  return `${trimBaseUrl(baseUrl)}${apis.voiceNav[name]}`
}

export function getVoiceNavWebSocketTranscribePath(): string {
  return apis.voiceNav.webSocketTranscribe
}

export function getMedicalLinkOrigin(): string {
  return trimBaseUrl(apis.medicalLink.origin)
}

export function isMedicalLinkHostname(hostname: string): boolean {
  const h = hostname.toLowerCase()
  const host = new URL(getMedicalLinkOrigin()).hostname.toLowerCase()
  return h === host || h.endsWith('.' + host)
}

export function isVoiceNavHostname(hostname: string, apiBaseUrl?: string): boolean {
  const h = hostname.toLowerCase()
  const base = (apiBaseUrl || getDefaultVoiceNavApiBaseFromCatalog()).trim()
  if (!base) return false
  try {
    const host = new URL(base).hostname.toLowerCase()
    return h === host || h.endsWith('.' + host)
  } catch {
    return false
  }
}

/** Sports NOC preview proxy path from the service catalog (e.g. `/chat/sports-certificate/preview`). */
export function sportsCertificatePreviewPath(): string {
  try {
    return new URL(buildVoiceNavUrl(getDefaultVoiceNavApiBaseFromCatalog(), 'sportsCertificatePreview'))
      .pathname
  } catch {
    return '/chat/sports-certificate/preview'
  }
}

/**
 * Rewrite backend-issued preview proxy URLs onto the configured API base (host/scheme).
 * Fixes dev/proxy setups where the server returns an internal host (e.g. EC2 IP:8000) but the
 * widget calls the public VoiceNav API base.
 */
export function coerceSportsCertificatePreviewUrl(
  raw: string,
  apiBaseUrl?: string,
): string | undefined {
  const u = (raw || '').trim()
  if (!u) return undefined
  const base = (apiBaseUrl || getDefaultVoiceNavApiBaseFromCatalog()).trim()
  if (!base) return undefined
  try {
    const parsed = new URL(u)
    const previewPath = sportsCertificatePreviewPath()
    if (
      !parsed.pathname.endsWith(previewPath) &&
      !parsed.pathname.includes('sports-certificate/preview')
    ) {
      return undefined
    }
    const canonical = new URL(buildVoiceNavUrl(base, 'sportsCertificatePreview'))
    const token = parsed.searchParams.get('preview_token')
    const fileUrl = parsed.searchParams.get('file_url')
    canonical.search = ''
    if (token) canonical.searchParams.set('preview_token', token)
    else if (fileUrl) canonical.searchParams.set('file_url', fileUrl)
    else {
      parsed.searchParams.forEach((value, key) => {
        canonical.searchParams.set(key, value)
      })
    }
    return canonical.toString()
  } catch {
    return undefined
  }
}

/** Build a preview proxy URL from a token when only wire metadata is available. */
export function buildSportsCertificatePreviewUrlFromToken(
  previewToken: string,
  apiBaseUrl?: string,
): string | undefined {
  const token = (previewToken || '').trim()
  if (!token) return undefined
  const base = (apiBaseUrl || getDefaultVoiceNavApiBaseFromCatalog()).trim()
  if (!base) return undefined
  try {
    const u = new URL(buildVoiceNavUrl(base, 'sportsCertificatePreview'))
    u.searchParams.set('preview_token', token)
    return u.toString()
  } catch {
    return undefined
  }
}

function expandAppPath(
  key: keyof FrontendApiConfig['medicalLink']['app'],
  params: Record<string, string>,
): string {
  let path: string = apis.medicalLink.app[key]
  for (const [k, v] of Object.entries(params)) {
    path = path.split(`{${k}}`).join(v)
  }
  return path
}

function medicalLinkAbsolutePath(
  key: keyof FrontendApiConfig['medicalLink']['app'],
  params: Record<string, string>,
): string {
  return `${getMedicalLinkOrigin()}${expandAppPath(key, params)}`
}

export function medilinkRestPath<K extends keyof MedicalLinkRest>(key: K): MedicalLinkRest[K] {
  return apis.medicalLink.rest[key]
}

export function getUnreadMessagesPath(): string {
  return medilinkRestPath('doctorGetUnreadMessages')
}

export function getPatientLastMessagesPath(): string {
  return medilinkRestPath('patientGetLastMessages')
}

export function getRecentOrdBioDetailPath(): string {
  return medilinkRestPath('recentOrdBioDetail')
}

/**
 * MediKaLLink HTTP paths for mail, messaging, and reminders (see backend workflows).
 * Full URL: `getMedicalLinkOrigin()` + path.
 */
export const medicalLinkIntentApiPaths = {
  mail: { sendEmail: medilinkRestPath('sendEmail') },
  messaging: {
    searchPatient: medilinkRestPath('searchPatient'),
    patientList: medilinkRestPath('patientList'),
    discussionInitiate: medilinkRestPath('discussionInitiate'),
    usersAutofill: medilinkRestPath('usersAutofill'),
  },
  reminders: {
    addReminderTask: medilinkRestPath('doctorAddReminderTask'),
  },
} as const

export function getMedicalLinkIntentPath<
  G extends keyof typeof medicalLinkIntentApiPaths,
  N extends keyof (typeof medicalLinkIntentApiPaths)[G] & string,
>(group: G, name: N): string {
  const block = medicalLinkIntentApiPaths[group] as Record<string, string>
  return block[name] as string
}

export function getUnreadMessagesUrl(urgent: 0 | 1 = 0): string {
  const u = new URL(`${getMedicalLinkOrigin()}${getUnreadMessagesPath()}`)
  u.searchParams.set('urgent', String(urgent))
  return u.toString()
}

export function getPatientLastMessagesUrl(fbUid: string): string {
  const u = new URL(`${getMedicalLinkOrigin()}${getPatientLastMessagesPath()}`)
  u.searchParams.set('fb_uid', (fbUid || '').trim())
  return u.toString()
}

export function buildRecentOrdBioDetailUrl(params: {
  patientId: string
  bioType: string
  filterType: 1 | 2
  dateFilter?: string
}): string {
  const u = new URL(`${getMedicalLinkOrigin()}${getRecentOrdBioDetailPath()}`)
  u.searchParams.set('patient_id', params.patientId)
  u.searchParams.set('bio_type', params.bioType)
  u.searchParams.set('filter_type', String(params.filterType))
  if (params.filterType === 2 && params.dateFilter) {
    u.searchParams.set('date_filter', params.dateFilter)
  }
  return u.toString()
}

/**
 * @see buildMessagingInboxUrl / buildMessagingThreadUrl in `buildMessagingThreadUrl.ts`
 */
export function buildMedicalLinkMessagingInboxUrl(loggedUserId: string): string {
  const uid = (loggedUserId || '').trim()
  if (!uid || !/^[0-9a-fA-F-]{36}$/.test(uid)) {
    return `${getMedicalLinkOrigin()}/`
  }
  return medicalLinkAbsolutePath('messagingInbox', { userId: encodeURIComponent(uid) })
}

/** In-app menu URL after route + user id are validated; see `buildNavigationPageUrlFromRoute` in the widget. */
export function buildMedicalLinkNavigationUrl(userId: string, menuRoute: string): string {
  return medicalLinkAbsolutePath('navigationMenu', { userId, menuRoute })
}

export function buildMedicalLinkPatientDossierUrl(
  loggedUserId: string,
  patientId: string,
): string {
  return medicalLinkAbsolutePath('patientDossier', {
    userId: (loggedUserId || '').trim(),
    patientId: encodeURIComponent((patientId || '').trim()),
  })
}

export function getSportsCertificateCommentsFieldId(): number {
  return apis.medicalLink.sportsCertificate.commentsFieldId
}

export function getSportsCertificateFieldsUrl(): string {
  return `${getMedicalLinkOrigin()}${medilinkRestPath('sportsCertificateFields')}`
}

export function getVoiceNavSportsCertificateAccessibleLocationsPath(): string {
  return apis.voiceNav.sportsCertificateAccessibleLocations
}

export function getPatientSportsCertificateUrl(): string {
  return `${getMedicalLinkOrigin()}${medilinkRestPath('sportsCertificateCreate')}`
}

/** Microsoft Office Online viewer base (Word embed); query `src` appended by caller. */
export function getMicrosoftOfficeEmbedViewerBaseUrl(): string {
  return trimBaseUrl(apis.external.microsoftOfficeEmbedViewerBaseUrl)
}

export function buildOfficeEmbedViewerUrl(documentUrl: string): string {
  return `${getMicrosoftOfficeEmbedViewerBaseUrl()}?src=${encodeURIComponent(documentUrl)}`
}

export type VoiceNavSessionActivePatientPayload = {
  patient_id?: string
  patient_name?: string
  dob?: string
  patient_unique_id?: string
  patient_fb_id?: string
}

export type VoiceNavSessionPendingUiReplayStatus = {
  deferred: boolean
  replay_ready: boolean
  expired: boolean
  /** Server armed replay after patient switch (Redis copilot session; survives full page reload). */
  armed?: boolean
  /** Temporary debug: e.g. ``open_modality_exam:ecg`` when a deferred UI action is queued. */
  pending_command?: string | null
}

/** Mid-turn \"open ECG of X\" / view prescription of X while another chart is active — see ``pending_action`` on server. */
export type VoiceNavPendingOpenModalityWorkflow =
  | {
      kind: 'open_modality_for_patient'
      target_patient_name: string
      modality: string
      recency: string
      awaiting_switch_confirm: boolean
      /** Same label family as ``pending_command`` on ``pending_ui_replay``. */
      then_open: string
    }
  | {
      kind: 'view_prescription_for_patient'
      target_patient_name: string
      prescription_type: string
      awaiting_switch_confirm: boolean
      then_open: string
    }
  | {
      kind: 'sports_noc_for_patient'
      target_patient_name: string
      awaiting_switch_confirm: boolean
      then_open: string
    }
  | {
      kind: 'renew_ordonnance_for_patient'
      target_patient_name: string
      prescription_type: string
      renew_days: number
      original_duration_text?: string | null
      awaiting_switch_confirm: boolean
      then_open: string
    }

export type VoiceNavDeferredOpenEcgStatus = {
  pending?: boolean
  expired?: boolean
  replay_ready?: boolean
  action_type?: string
}

export type VoiceNavSportsNocWorkflowSnapshot = {
  active?: boolean
  /** When true, widget must route typed/voice input to ``/chat/command`` (not host sendMessage). */
  use_hosted_chat?: boolean
  awaiting_field?: string | null
  french?: boolean
  location_id?: number | null
  location_name?: string | null
  pending_selected_sport_ids?: number[]
  resolved_patient_id?: string | null
  resolved_patient_name?: string | null
  needs_clarification?: boolean
  yes_no_kind?: string | null
  certificate_generated?: boolean
  mail_send_completed?: boolean
}

export type VoiceNavSessionContextResponse = {
  authenticated?: boolean
  logged_user_id?: string
  active_patient?: VoiceNavSessionActivePatientPayload | null
  pending_ui_replay?: VoiceNavSessionPendingUiReplayStatus
  /** Queued modality step before ``pending_ui_action`` exists (e.g. waiting for switch Yes). */
  pending_open_modality_workflow?: VoiceNavPendingOpenModalityWorkflow | null
  /** Cross-patient deferred ECG after switch Yes (single session Redis field; consumed by POST ``pending-ui-replay`` when chart matches). */
  deferred_open_ecg?: VoiceNavDeferredOpenEcgStatus | null
  active_workflows?: {
    sports_noc?: VoiceNavSportsNocWorkflowSnapshot | null
  }
}

export async function postVoiceNavWorkflowAction(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string },
  body:
    | {
        workflow: 'sports_noc'
        action:
          | 'toggle_sport'
          | 'select_location'
          | 'submit_sports'
          | 'preview_opened'
          | 'edit_location'
          | 'edit_sports'
          | 'edit_comments'
          | 'submit_comments'
          | 'cancel_summary_edit'
        field_id?: number
        location_id?: number
        preview_token?: string
        comments_text?: string
        inline?: boolean
      }
    | {
        workflow: 'session'
        action: 'cancel_intent'
        language?: string
      },
): Promise<{
  ok?: boolean
  error?: string
  message?: string
  assistant_messages?: Array<{ text?: string; metadata?: Record<string, unknown> }>
  pending_selected_sport_ids?: number[]
  workflow?: VoiceNavSportsNocWorkflowSnapshot | null
  location_name?: string
  edit_context?: Record<string, unknown>
  summary?: Record<string, unknown>
} | null> {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return null
  const u = new URL(buildVoiceNavUrl(baseUrl, 'sessionWorkflowAction'))
  u.searchParams.set('logged_user_id', uid)
  const res = await fetch(u.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    try {
      const errBody = (await res.json()) as { detail?: string; error?: string; message?: string }
      const detail = (errBody.detail || errBody.error || '').trim()
      return {
        ok: false,
        error: detail || `HTTP ${res.status}`,
        message: (errBody.message || '').trim() || undefined,
      }
    } catch {
      return { ok: false, error: `HTTP ${res.status}` }
    }
  }
  return (await res.json()) as {
    ok?: boolean
    error?: string
    message?: string
    assistant_messages?: Array<{ text?: string; metadata?: Record<string, unknown> }>
    pending_selected_sport_ids?: number[]
    workflow?: VoiceNavSportsNocWorkflowSnapshot | null
    location_name?: string
    edit_context?: Record<string, unknown>
    summary?: Record<string, unknown>
  }
}

export async function fetchVoiceNavSessionContext(
  baseUrl: string,
  creds: {
    piBearerToken: string
    loggedUserId: string
    medCenterId?: string
    loggedUserType?: string
    loggedUserName?: string
    senderFbUid?: string
  },
): Promise<VoiceNavSessionContextResponse | null> {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return null
  const u = new URL(buildVoiceNavUrl(baseUrl, 'sessionContext'))
  u.searchParams.set('logged_user_id', uid)
  const userType = (creds.loggedUserType ?? '').trim()
  if (userType) u.searchParams.set('logged_user_type', userType)
  const userName = (creds.loggedUserName ?? '').trim()
  if (userName) u.searchParams.set('logged_user_name', userName)
  const mc = (creds.medCenterId ?? '').trim()
  if (mc) u.searchParams.set('med_center_id', mc)
  const fb = (creds.senderFbUid ?? '').trim()
  if (fb) u.searchParams.set('sender_fb_uid', fb)
  const res = await fetch(u.toString(), { headers: { Authorization: `Bearer ${token}` } })
  if (!res.ok) return null
  return (await res.json()) as VoiceNavSessionContextResponse
}

export async function postVoiceNavSessionActivePatient(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string; medCenterId?: string },
  body: VoiceNavSessionActivePatientPayload,
): Promise<VoiceNavSessionActivePatientPayload | null> {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return null
  const u = new URL(buildVoiceNavUrl(baseUrl, 'sessionActivePatient'))
  u.searchParams.set('logged_user_id', uid)
  const mc = (creds.medCenterId ?? '').trim()
  if (mc) u.searchParams.set('med_center_id', mc)
  const res = await fetch(u.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) return null
  const data = (await res.json()) as { active_patient?: VoiceNavSessionActivePatientPayload | null }
  return data.active_patient ?? null
}

/** Replay deferred show_modality after active patient was committed (backend ``pending_ui_action``). */
export async function postVoiceNavSessionPendingUiReplay(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string; medCenterId?: string },
): Promise<{ ok?: boolean; replayed?: boolean; assistant_messages?: unknown[] } | null> {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return null
  const u = new URL(buildVoiceNavUrl(baseUrl, 'sessionPendingUiReplay'))
  u.searchParams.set('logged_user_id', uid)
  const mc = (creds.medCenterId ?? '').trim()
  if (mc) u.searchParams.set('med_center_id', mc)
  const res = await fetch(u.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (!res.ok) {
    return {
      httpOk: false,
      httpStatus: res.status,
      ok: false,
      replayed: false,
      assistant_messages: [],
    }
  }
  const body = (await res.json()) as {
    ok?: boolean
    replayed?: boolean
    assistant_messages?: unknown[]
  }
  return { ...body, httpOk: true, httpStatus: res.status }
}

export async function deleteVoiceNavSessionActivePatient(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string },
): Promise<boolean> {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return false
  const u = new URL(buildVoiceNavUrl(baseUrl, 'sessionActivePatient'))
  u.searchParams.set('logged_user_id', uid)
  const res = await fetch(u.toString(), { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
  return res.ok
}

export async function fetchVoiceNavChatHistory(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string },
): Promise<{ messages: SessionChatWireMessage[]; logged_user_id?: string } | null> {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return null
  const u = new URL(buildVoiceNavUrl(baseUrl, 'sessionChatHistory'))
  u.searchParams.set('logged_user_id', uid)
  const res = await fetch(u.toString(), { headers: { Authorization: `Bearer ${token}` } })
  if (voicnavSessionClientDebug()) {
    // eslint-disable-next-line no-console
    console.info('[voicnav_debug_session] chat_history GET', { status: res.status, ok: res.ok })
  }
  if (res.status === 401 || res.status === 403) return null
  if (!res.ok) {
    if (voicnavSessionClientDebug()) {
      // eslint-disable-next-line no-console
      console.warn('[voicnav_debug_session] chat_history GET non-OK', res.status)
    }
    return null
  }
  const payload = (await res.json()) as { messages: SessionChatWireMessage[]; logged_user_id?: string }
  if (voicnavSessionClientDebug()) {
    // eslint-disable-next-line no-console
    console.info('[voicnav_debug_session] chat_history GET body', { messageCount: payload.messages?.length ?? 0 })
  }
  return payload
}

function sessionChatHistoryPostUrl(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string },
): URL | null {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return null
  const u = new URL(buildVoiceNavUrl(baseUrl, 'sessionChatHistory'))
  u.searchParams.set('logged_user_id', uid)
  return u
}

export async function replaceVoiceNavSessionChatHistory(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string },
  messages: SessionChatWireMessage[],
): Promise<boolean> {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return false
  const u = sessionChatHistoryPostUrl(baseUrl, creds)
  if (!u) return false
  const res = await fetch(u.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action: 'replace', messages }),
  })
  if (voicnavSessionClientDebug()) {
    // eslint-disable-next-line no-console
    console.info('[voicnav_debug_session] chat_history REPLACE', {
      status: res.status,
      ok: res.ok,
      messageCount: messages.length,
    })
  }
  return res.ok
}

/** Best-effort flush before tab close (pairs with server commit on /chat/command). */
export function flushVoiceNavSessionChatHistory(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string },
  messages: SessionChatWireMessage[],
): void {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return
  const u = sessionChatHistoryPostUrl(baseUrl, creds)
  if (!u) return
  const body = JSON.stringify({ action: 'replace', messages })
  try {
    void fetch(u.toString(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body,
      keepalive: true,
    })
  } catch {
    // best-effort
  }
}

export async function deleteVoiceNavSessionChatHistory(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string },
): Promise<void> {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return
  const u = new URL(buildVoiceNavUrl(baseUrl, 'sessionChatHistory'))
  u.searchParams.set('logged_user_id', uid)
  try {
    await fetch(u.toString(), { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
  } catch {
    // best-effort
  }
}

export async function postVoiceNavLogout(
  baseUrl: string,
  creds: { piBearerToken: string; loggedUserId: string },
): Promise<void> {
  const token = creds.piBearerToken.trim()
  const uid = creds.loggedUserId.trim()
  if (!token || !uid) return
  const u = new URL(buildVoiceNavUrl(baseUrl, 'logout'))
  u.searchParams.set('logged_user_id', uid)
  try {
    await fetch(u.toString(), { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
  } catch {
    // best-effort
  }
}

export async function fetchVoiceNavVersionInfo(
  baseUrl: string,
  signal?: AbortSignal,
): Promise<VoiceNavVersionInfo | null> {
  const res = await fetch(buildVoiceNavUrl(baseUrl, 'versionInfo'), { signal })
  if (!res.ok) return null
  const data = (await res.json()) as Partial<VoiceNavVersionInfo>
  return {
    status: String(data.status ?? '').trim(),
    service: String(data.service ?? '').trim(),
    version: String(data.version ?? '').trim(),
    started_at: String(data.started_at ?? '').trim(),
    started_at_label: String(data.started_at_label ?? '').trim(),
    transcribe_mode: Number(data.transcribe_mode) === 2 ? 2 : 1,
  }
}

/** Temporary French gTTS MP3 for local ASR loop testing (`POST /tts/gtts`). */
export async function fetchGttsFrenchAudio(baseUrl: string, text: string): Promise<Blob> {
  const res = await fetch(buildVoiceNavUrl(baseUrl, 'ttsGtts'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: text.trim() }),
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(detail.trim() || `gTTS HTTP ${res.status}`)
  }
  const contentType = (res.headers.get('Content-Type') || '').toLowerCase()
  if (!contentType.includes('audio')) {
    throw new Error('Invalid gTTS response — is the backend running with gTTS installed?')
  }
  return res.blob()
}

export async function postTranscribeVoice(
  baseUrl: string,
  form: FormData,
  bearerToken?: string,
): Promise<TranscribeVoiceResponse> {
  const headers: Record<string, string> = {}
  const token = (bearerToken ?? '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(buildVoiceNavUrl(baseUrl, 'transcribeVoice'), {
    method: 'POST',
    body: form,
    headers,
  })
  if (!res.ok) {
    let detail = res.statusText
    try {
      const err = (await res.json()) as { detail?: unknown }
      if (typeof err.detail === 'string') detail = err.detail
      else if (Array.isArray(err.detail)) detail = err.detail.map(String).join('; ')
    } catch {
      // ignore
    }
    throw new Error(detail || 'Batch voice transcription failed.')
  }
  return (await res.json()) as TranscribeVoiceResponse
}
